import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  ExternalLink,
  Video,
  FileText,
  FileCode,
  Link as LinkIcon,
  File,
  Eye,
  Tag,
  FolderOpen,
  Copy,
  Check,
  Clock,
  Loader2,
  X
} from 'lucide-react';
import { agentResources } from '../../utils/api';

/**
 * AgentResourceDetail - Displays a single resource with embedded content for agents
 * This is a read-only view with support for embedding videos, documents, and showing content
 * @param {object} resource - The resource object to display (optional - can also fetch by ID)
 * @param {string} resourceId - The resource ID to fetch (optional - used if resource not provided)
 * @param {function} onBack - Callback when back button is clicked
 */
const AgentResourceDetail = ({ resource: initialResource, resourceId, onBack }) => {
  const [resource, setResource] = useState(initialResource);
  const [loading, setLoading] = useState(!initialResource);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [videoEmbedError, setVideoEmbedError] = useState(false);

  useEffect(() => {
    // If we have an initial resource, use it
    if (initialResource) {
      setResource(initialResource);
      setLoading(false);
      // Still record the view
      recordView(initialResource._id);
      return;
    }

    // Otherwise fetch by ID if provided
    if (resourceId) {
      fetchResource();
    }
  }, [initialResource, resourceId]);

  const fetchResource = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await agentResources.agent.getAll({ search: '' });
      const foundResource = response.data?.data?.find(r => r._id === resourceId);

      if (foundResource) {
        setResource(foundResource);
        recordView(resourceId);
      } else {
        setError('Resource not found');
      }
    } catch (err) {
      console.error('Error fetching resource:', err);
      setError('Failed to load resource');
    } finally {
      setLoading(false);
    }
  };

  const recordView = async (id) => {
    try {
      await agentResources.agent.recordView(id);
    } catch (err) {
      console.error('Error recording view:', err);
    }
  };

  const copyLink = () => {
    if (resource?.url) {
      navigator.clipboard.writeText(resource.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      video: Video,
      link: LinkIcon,
      document: FileText,
      script: FileCode,
      file: File
    };
    const Icon = icons[type] || File;
    return <Icon className="w-6 h-6" />;
  };

  const getTypeLabel = (type) => {
    const labels = {
      video: 'Video',
      link: 'Link',
      document: 'Document',
      script: 'Script',
      file: 'File'
    };
    return labels[type] || type;
  };

  // Video embedding utilities
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      let videoId;
      if (urlObj.hostname.includes('youtu.be')) {
        videoId = urlObj.pathname.slice(1);
      } else if (urlObj.hostname.includes('youtube.com')) {
        videoId = urlObj.searchParams.get('v');
      }
      return videoId || null;
    } catch {
      return null;
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null;
  };

  const getVimeoEmbedUrl = (url) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('vimeo.com')) {
        const videoId = urlObj.pathname.split('/').pop();
        if (videoId) {
          return `https://player.vimeo.com/video/${videoId}`;
        }
      }
    } catch {
      return null;
    }
    return null;
  };

  const getLoomEmbedUrl = (url) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('loom.com')) {
        const videoId = urlObj.pathname.split('/').pop();
        if (videoId) {
          return `https://www.loom.com/embed/${videoId}`;
        }
      }
    } catch {
      return null;
    }
    return null;
  };

  // Google Docs embedding utilities
  const getGoogleDocsEmbedUrl = (url) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      // Handle Google Docs
      if (urlObj.hostname.includes('docs.google.com')) {
        const pathParts = urlObj.pathname.split('/');
        const docType = pathParts[1]; // document, spreadsheets, presentation
        const docIdIndex = pathParts.indexOf('d') + 1;
        const docId = pathParts[docIdIndex];

        if (docId) {
          if (docType === 'document') {
            return `https://docs.google.com/document/d/${docId}/preview`;
          } else if (docType === 'spreadsheets') {
            return `https://docs.google.com/spreadsheets/d/${docId}/preview`;
          } else if (docType === 'presentation') {
            return `https://docs.google.com/presentation/d/${docId}/embed?start=false&loop=false&delayms=3000`;
          }
        }
      }
    } catch {
      return null;
    }
    return null;
  };

  const renderVideoEmbed = () => {
    if (!resource?.url || resource.type !== 'video') return null;

    const youtubeUrl = getYouTubeEmbedUrl(resource.url);
    const vimeoUrl = getVimeoEmbedUrl(resource.url);
    const loomUrl = getLoomEmbedUrl(resource.url);

    const embedUrl = youtubeUrl || vimeoUrl || loomUrl;
    const isYouTube = !!youtubeUrl;

    // Handle iframe load error or show fallback
    const handleIframeError = () => {
      setVideoEmbedError(true);
    };

    if (embedUrl && !videoEmbedError) {
      return (
        <div id="agent-video-embed" className="space-y-3">
          <div className="aspect-video rounded-lg overflow-hidden bg-black shadow-lg">
            <iframe
              src={embedUrl}
              title={resource.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              onError={handleIframeError}
            />
          </div>
          {/* Always show "Watch on YouTube" button for YouTube videos */}
          {isYouTube && (
            <a
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
            >
              <Video className="w-4 h-4 mr-2" />
              Watch on YouTube
              <ExternalLink className="w-3 h-3 ml-2" />
            </a>
          )}
        </div>
      );
    }

    // Fallback view when embed fails or isn't available
    return (
      <div id="agent-video-fallback" className="space-y-4">
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block aspect-video rounded-lg overflow-hidden bg-gray-100 relative group shadow-lg"
        >
          {resource.metadata?.thumbnailUrl ? (
            <img
              src={resource.metadata.thumbnailUrl}
              alt={resource.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-100 to-red-200">
              <Video className="w-16 h-16 text-red-400" />
            </div>
          )}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-4 shadow-lg">
              <ExternalLink className="w-8 h-8 text-gray-700" />
            </div>
          </div>
        </a>
        {/* Show "Watch on YouTube" button for YouTube videos when embed fails */}
        {isYouTube && (
          <div className="flex items-center gap-3">
            <a
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
            >
              <Video className="w-4 h-4 mr-2" />
              Watch on YouTube
              <ExternalLink className="w-3 h-3 ml-2" />
            </a>
            {videoEmbedError && (
              <span className="text-sm text-gray-500">
                Video embed unavailable
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDocumentEmbed = () => {
    if (!resource?.url) return null;

    const googleDocsUrl = getGoogleDocsEmbedUrl(resource.url);

    if (googleDocsUrl) {
      return (
        <div id="agent-document-embed" className="rounded-lg overflow-hidden border border-gray-200 shadow-lg" style={{ height: '600px' }}>
          <iframe
            src={googleDocsUrl}
            title={resource.title}
            className="w-full h-full"
            frameBorder="0"
          />
        </div>
      );
    }

    // Fallback to link
    return null;
  };

  const renderLinkPreview = () => {
    if (!resource?.url || resource.type !== 'link') return null;

    // Try to embed Google Docs first
    const googleDocsUrl = getGoogleDocsEmbedUrl(resource.url);
    if (googleDocsUrl) {
      return (
        <div id="agent-link-embed" className="rounded-lg overflow-hidden border border-gray-200 shadow-lg" style={{ height: '600px' }}>
          <iframe
            src={googleDocsUrl}
            title={resource.title}
            className="w-full h-full"
            frameBorder="0"
          />
        </div>
      );
    }

    // Regular link preview card
    return (
      <div id="agent-link-preview" className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="p-3 bg-blue-100 rounded-lg flex-shrink-0">
              <LinkIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500 mb-1">External Resource</p>
              <p className="text-gray-700 text-sm truncate max-w-md">
                {resource.url}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={copyLink}
              className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Copy link"
            >
              {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
            </button>
            <a
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Link
            </a>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!resource?.content) return null;

    return (
      <div id="agent-content-display" className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2 bg-gray-50">
          {resource.type === 'script' ? (
            <>
              <FileCode className="w-5 h-5 text-purple-500" />
              <span className="font-medium text-gray-700">Script</span>
            </>
          ) : (
            <>
              <FileText className="w-5 h-5 text-green-500" />
              <span className="font-medium text-gray-700">Document</span>
            </>
          )}
        </div>
        <div className="p-6">
          <div
            className={`whitespace-pre-wrap text-gray-800 leading-relaxed ${
              resource.type === 'script' ? 'font-mono text-sm bg-gray-50 p-4 rounded-lg border border-gray-200' : ''
            }`}
          >
            {resource.content}
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div id="agent-resource-loading" className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mb-4" />
          <p className="text-gray-500">Loading resource...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !resource) {
    return (
      <div id="agent-resource-error" className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <button
          onClick={onBack}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Resources
        </button>
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FolderOpen className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Resource Not Found</h3>
          <p className="text-gray-600">{error || 'This resource may have been removed or you may not have access.'}</p>
        </div>
      </div>
    );
  }

  // Category colors
  const categoryColors = {
    'Scripts & Dialogues': { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'text-blue-500' },
    'Training Videos': { bg: 'bg-purple-100', text: 'text-purple-700', icon: 'text-purple-500' },
    'Guides & Checklists': { bg: 'bg-green-100', text: 'text-green-700', icon: 'text-green-500' },
    'Downloads & Templates': { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'text-amber-500' },
    'default': { bg: 'bg-gray-100', text: 'text-gray-700', icon: 'text-gray-500' }
  };
  const colors = categoryColors[resource.category] || categoryColors['default'];

  return (
    <div id="agent-resource-detail" className="bg-white rounded-xl shadow-sm border border-gray-100">
      {/* Header */}
      <div id="agent-resource-header" className="p-6 border-b border-gray-200">
        <button
          onClick={onBack}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4 text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Resources
        </button>

        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${colors.bg} flex-shrink-0`}>
            <span className={colors.icon}>
              {getTypeIcon(resource.type)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {resource.title}
            </h1>
            {resource.description && (
              <p className="text-gray-600 leading-relaxed">
                {resource.description}
              </p>
            )}

            {/* Meta info row */}
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-500">
              {resource.category && (
                <span className={`px-2.5 py-1 rounded-full ${colors.bg} ${colors.text} font-medium`}>
                  {resource.category}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {resource.analytics?.views || 0} views
              </span>
              {resource.metadata?.duration && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {resource.metadata.duration}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-6 space-y-6">
        {/* Video Embed */}
        {resource.type === 'video' && renderVideoEmbed()}

        {/* Document Embed or Content */}
        {(resource.type === 'document' || resource.type === 'script') && (
          <>
            {resource.url && renderDocumentEmbed()}
            {resource.content && renderContent()}
            {resource.url && !getGoogleDocsEmbedUrl(resource.url) && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`p-3 rounded-lg ${colors.bg} flex-shrink-0`}>
                      {getTypeIcon(resource.type)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-500 mb-1">Document Link</p>
                      <p className="text-gray-700 text-sm truncate max-w-md">
                        {resource.url}
                      </p>
                    </div>
                  </div>
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Document
                  </a>
                </div>
              </div>
            )}
          </>
        )}

        {/* Link Preview */}
        {resource.type === 'link' && renderLinkPreview()}

        {/* Tags */}
        {resource.tags && resource.tags.length > 0 && (
          <div id="agent-resource-tags" className="pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-600">Tags</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {resource.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentResourceDetail;
